
import { Component, Vue } from "vue-property-decorator";
import { AttachmentMeta, KnowledgeBaseArticle } from "@/survey";
import PdfGenerator from "@/export/pdf";

@Component({})
export default class KnowledgeBaseArticleView extends Vue {
  article: KnowledgeBaseArticle | null = null;
  error = "";
  deleting = false;
  exporting = false;
  apiLocation = Vue.prototype.$apiLocation;

  get canExport() {
    const user = this.$store.state.authentication.currentUser;
    return user !== null && (user.admin || user.export);
  }

  get canEditArticle() {
    const user = this.$store.state.authentication.currentUser;
    if (user === null || this.article === null) {
      return false;
    }
    return user.admin || (user.edit && user.id !== null && user.id !== undefined && user.id === this.article.authorId);
  }

  get canDeleteArticle() {
    const user = this.$store.state.authentication.currentUser;
    if (user === null || this.article === null) {
      return false;
    }
    return user.admin || (user.id !== null && user.id !== undefined && user.id === this.article.authorId);
  }

  get badgeStyle() {
    return {
      backgroundColor: this.article?.categoryColor || "#3157b7",
      color: "#fff"
    };
  }

  get tagList() {
    if (this.article === null) {
      return [];
    }
    const tags = (this.article.tags || "") as string;
    return tags.split(",").map((tag: string) => tag.trim()).filter((tag: string) => tag.length > 0);
  }

  get attachments(): AttachmentMeta[] {
    return (this.article?.attachmentMeta || []).filter(attachment => attachment.notes !== "Pasted into article body");
  }

  get sanitizedContent() {
    if (this.article === null) {
      return "";
    }
    const content = this.normalizeContentForHtml(this.article.content || "");
    const template = document.createElement("template");
    template.innerHTML = content;
    template.content.querySelectorAll("script, iframe, object, embed").forEach(element => element.remove());
    template.content.querySelectorAll("*").forEach((element: Element) => {
      Array.from(element.attributes).forEach(attribute => {
        const name = attribute.name.toLowerCase();
        const value = attribute.value.trim().toLowerCase();
        if (name.startsWith("on") || name === "dir" || value.startsWith("javascript:") || value.indexOf("direction:") >= 0) {
          element.removeAttribute(attribute.name);
        }
      });
    });
    return template.innerHTML;
  }

  normalizeContentForHtml(content: string) {
    if (!content || /<[^>]+>/.test(content)) {
      return content;
    }
    return content
      .split(/\n{2,}/)
      .map(paragraph => paragraph.trim())
      .filter(paragraph => paragraph.length > 0)
      .map(paragraph => `<p>${this.escapeHtml(paragraph).replace(/\n/g, "<br>")}</p>`)
      .join("");
  }

  escapeHtml(content: string) {
    return content
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  isImage(contentType: string | undefined) {
    return contentType !== undefined && contentType.indexOf("image/") === 0;
  }

  async downloadAttachment(id: number | undefined, fileName: string | undefined) {
    if (!id) {
      return;
    }
    const blob = await Vue.prototype.$surveyClient.downloadAttachment(id, "kb_article");
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "knowledge-base-attachment";
    link.click();
    window.URL.revokeObjectURL(url);
  }

  get pdfFileName() {
    // Strip characters that are illegal in Windows/macOS file names, plus any
    // control characters. Iterating by code point keeps surrogate pairs intact
    // and avoids a control-character range in a regex literal.
    const cleaned = Array.from(this.article?.title || "")
      .map(character => {
        const illegal = '\\/:*?"<>|';
        return character.charCodeAt(0) < 32 || illegal.indexOf(character) >= 0 ? " " : character;
      })
      .join("");
    const title = cleaned
      .replace(/\s+/g, " ")
      .replace(/^\.+/, "")
      .trim()
      .substring(0, 120)
      .trim();
    return `${title.length > 0 ? title : "knowledge-base-article"}.pdf`;
  }

  async exportPdf() {
    if (this.article === null) {
      return;
    }
    this.exporting = true;
    this.error = "";
    try {
      const pdfDoc = await new PdfGenerator().getKnowledgeBaseArticleDocument(this.article);
      pdfDoc.download(this.pdfFileName);
    } catch (error) {
      this.error = "Unable to export this knowledge base article to PDF.";
    } finally {
      this.exporting = false;
    }
  }

  async deleteArticle() {
    if (this.article === null || !this.article.id || !confirm("Delete this knowledge base article and all attachments?")) {
      return;
    }
    this.deleting = true;
    this.error = "";
    try {
      await Vue.prototype.$surveyClient.deleteKnowledgeBaseArticle(this.article.id);
      this.$router.push("/knowledge-base");
    } catch (error) {
      this.error = "Unable to delete the knowledge base article.";
    } finally {
      this.deleting = false;
    }
  }

  async created() {
    try {
      this.article = await Vue.prototype.$surveyClient.getKnowledgeBaseArticle(parseInt(this.$route.params.id));
    } catch (error) {
      this.error = "Unable to load the knowledge base article.";
    }
  }

  formatDate(value: number) {
    return new Date(value).toLocaleString();
  }
}
